import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { mxWeekBounds, addDaysStr } from '../../lib/mxWeek';

const PAGE_SIZE = 15;
const FETCH_CHUNK = 1000; // Supabase caps a single select at 1000 rows

/* ── Helpers ──────────────────────────────────────── */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return timeStr.slice(0, 5);
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function getContrastColor(hex) {
  const c = (hex || '').replace('#', '');
  if (c.length < 6) return '#222222';
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#222222' : '#FFFFFF';
}

/** Previous complete MX week (Mon–Sun) — the default report range. */
function lastWeekRange() {
  const { weekStartStr } = mxWeekBounds();
  return { from: addDaysStr(weekStartStr, -7), to: addDaysStr(weekStartStr, -1) };
}

/* ── Icons ────────────────────────────────────────── */
function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function IconClearFilter() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* ── Filter Picker Modal (same pattern as TimeLogReport) ── */
function FilterPickerModal({ open, title, options, value, onSelect, onClose, labelKey }) {
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const getLabel = (o) => typeof labelKey === 'function' ? labelKey(o) : o[labelKey || 'name'];

  const filtered = search.trim()
    ? options.filter((o) => getLabel(o).toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="act-fpm-backdrop" ref={backdropRef} onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="act-fpm-modal">
        <div className="act-fpm-header">
          <h3 className="act-fpm-title">{title}</h3>
          <button className="act-fpm-close" onClick={onClose} aria-label="Cerrar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="act-fpm-search-wrap">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input
            ref={inputRef}
            type="text"
            className="act-fpm-search"
            placeholder="Buscar…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <ul className="act-fpm-list">
          <li>
            <button
              className={`act-fpm-item${!value ? ' act-fpm-item--active' : ''}`}
              onClick={() => { onSelect(''); onClose(); }}
            >
              <span className="act-fpm-item-label">Todos</span>
            </button>
          </li>
          {filtered.map((o) => {
            const label = getLabel(o);
            const isActive = String(o.id) === String(value);
            return (
              <li key={o.id}>
                <button
                  className={`act-fpm-item${isActive ? ' act-fpm-item--active' : ''}`}
                  onClick={() => { onSelect(o.id); onClose(); }}
                >
                  <span className="act-fpm-item-label">{label}</span>
                  {isActive && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                  )}
                </button>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="act-fpm-empty">Sin resultados</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* ── Component ────────────────────────────────────── */
function ActivityReport({ onBack }) {
  const defaultRange = useMemo(() => lastWeekRange(), []);
  // Fill-ahead logs can exist up to the end of the current MX week
  const maxDate = useMemo(() => mxWeekBounds().weekEndStr, []);

  // Filters
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [filterUserId, setFilterUserId] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatusId, setFilterStatusId] = useState('');
  const [filterLeaderId, setFilterLeaderId] = useState('');

  // Catalogs
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Data
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // Pickers
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  // Guards against out-of-order responses when filters change quickly
  const fetchIdRef = useRef(0);

  const invalidRange = Boolean(dateFrom && dateTo && dateFrom > dateTo);

  /* ── Load catalogs ── */
  useEffect(() => {
    async function loadCatalogs() {
      const [usersRes, clientsRes, statusRes] = await Promise.all([
        supabase.from('users').select('id, full_name, roles(name)').eq('active', true).order('full_name'),
        supabase.from('clients').select('id, name').eq('active', true).order('name'),
        supabase.from('status').select('id, name, color').eq('active', true).order('name'),
      ]);
      setUsers(usersRes.data || []);
      setClients(clientsRes.data || []);
      setStatuses(statusRes.data || []);
    }
    loadCatalogs();
  }, []);

  /* Líder filter options: same population as the Requirements leader filter */
  const leaderOptions = useMemo(
    () => users.filter((u) => u.roles?.name === 'Gerente' || u.roles?.name === 'Líder'),
    [users]
  );

  /* ── Fetch logs (server-side: date range + resource; chunked past 1000 rows) ── */
  const fetchLogs = useCallback(async () => {
    if (!dateFrom || !dateTo || invalidRange) return;
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setFetchError('');

    const all = [];
    let offset = 0;
    let error = null;

    for (;;) {
      let query = supabase
        .from('time_logs')
        .select(`
          id, log_date, start_time, end_time, total_hours,
          is_extended_hours, extended_hours, description,
          user:users!user_id(id, full_name),
          activity:activities!activity_id(id, name, hour_types(name)),
          requirement:requirements!requirement_id(
            id, reference_id,
            status_rel:status!status_id(id, name, color),
            leader:users!leader_user_id(id, full_name),
            project:projects!project_id(id, name, client:clients!client_id(id, name))
          )
        `)
        .gte('log_date', dateFrom)
        .lte('log_date', dateTo)
        .order('log_date', { ascending: false })
        .order('start_time', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + FETCH_CHUNK - 1);

      if (filterUserId) query = query.eq('user_id', filterUserId);

      const res = await query;
      if (res.error) { error = res.error; break; }
      all.push(...(res.data || []));
      if (!res.data || res.data.length < FETCH_CHUNK) break;
      offset += FETCH_CHUNK;
    }

    if (fetchId !== fetchIdRef.current) return; // a newer fetch superseded this one

    if (error) {
      setLogs([]);
      setFetchError('No se pudieron cargar los registros. Intenta de nuevo.');
    } else {
      setLogs(all);
    }
    setLoading(false);
    setPage(0);
  }, [dateFrom, dateTo, filterUserId, invalidRange]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  /* ── Client-side filters (requirement-derived fields) ──
   * Logs without requirement (actividades N/A) match only when the
   * requirement-based filters are empty. */
  const filteredLogs = useMemo(() => {
    return logs.filter((l) => {
      if (filterClientId && String(l.requirement?.project?.client?.id ?? '') !== String(filterClientId)) return false;
      if (filterStatusId && String(l.requirement?.status_rel?.id ?? '') !== String(filterStatusId)) return false;
      if (filterLeaderId && String(l.requirement?.leader?.id ?? '') !== String(filterLeaderId)) return false;
      return true;
    });
  }, [logs, filterClientId, filterStatusId, filterLeaderId]);

  /* reset page when client-side filters change */
  useEffect(() => { setPage(0); }, [filterClientId, filterStatusId, filterLeaderId]);

  /* ── Summary ── */
  const summary = useMemo(() => {
    let normal = 0, extended = 0;
    const resources = new Set();
    filteredLogs.forEach((l) => {
      normal += l.total_hours || 0;
      extended += l.extended_hours || 0;
      if (l.user?.id) resources.add(l.user.id);
    });
    return {
      total: round2(normal + extended),
      extended: round2(extended),
      records: filteredLogs.length,
      resources: resources.size,
    };
  }, [filteredLogs]);

  /* ── Pagination ── */
  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const pagedLogs = filteredLogs.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const hasActiveFilters = filterUserId || filterClientId || filterStatusId || filterLeaderId;

  function clearFilters() {
    setFilterUserId('');
    setFilterClientId('');
    setFilterStatusId('');
    setFilterLeaderId('');
  }

  const filterLabels = useMemo(() => ({
    user: filterUserId ? (users.find((u) => String(u.id) === String(filterUserId))?.full_name || '') : '',
    client: filterClientId ? (clients.find((c) => String(c.id) === String(filterClientId))?.name || '') : '',
    status: filterStatusId ? (statuses.find((s) => String(s.id) === String(filterStatusId))?.name || '') : '',
    leader: filterLeaderId ? (leaderOptions.find((u) => String(u.id) === String(filterLeaderId))?.full_name || '') : '',
  }), [filterUserId, filterClientId, filterStatusId, filterLeaderId, users, clients, statuses, leaderOptions]);

  /* ── Excel export ─────────────────────────────────
   * exceljs is imported lazily so it stays out of the main bundle. */
  async function exportToExcel() {
    if (!filteredLogs.length || exporting) return;
    setExporting(true);
    setExportError('');
    try {
      const mod = await import('exceljs');
      const ExcelJS = mod.default ?? mod;

      const ROYAL = 'FF00115D';
      const CARIBBEAN = 'FF00CFC1';
      const ZEBRA = 'FFF8F9FA';
      const thinBorder = {
        top: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        left: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        bottom: { style: 'thin', color: { argb: 'FFD9DCE5' } },
        right: { style: 'thin', color: { argb: 'FFD9DCE5' } },
      };

      const wb = new ExcelJS.Workbook();
      wb.creator = 'T.R.A.C.K.';
      wb.created = new Date();

      const ws = wb.addWorksheet('Reporte de Actividades', {
        views: [{ state: 'frozen', ySplit: 4 }],
      });

      const columns = [
        { header: 'Fecha', width: 12 },
        { header: 'Recurso', width: 26 },
        { header: 'Folio', width: 16 },
        { header: 'Cliente', width: 22 },
        { header: 'Proyecto', width: 22 },
        { header: 'Líder', width: 26 },
        { header: 'Estatus', width: 18 },
        { header: 'Actividad', width: 24 },
        { header: 'Tipo de Hora', width: 15 },
        { header: 'Hora Inicio', width: 11 },
        { header: 'Hora Fin', width: 11 },
        { header: 'Horas', width: 9 },
        { header: 'Horas Ext.', width: 11 },
        { header: 'Total', width: 9 },
        { header: 'Descripción', width: 50 },
      ];
      const colCount = columns.length;
      columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

      /* Title block */
      ws.mergeCells(1, 1, 1, colCount);
      const titleCell = ws.getCell(1, 1);
      titleCell.value = 'T.R.A.C.K. — Reporte de Actividades';
      titleCell.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
      titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
      ws.getRow(1).height = 30;

      ws.mergeCells(2, 1, 2, colCount);
      const periodCell = ws.getCell(2, 1);
      periodCell.value = `Período: ${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
      periodCell.font = { bold: true, size: 11, color: { argb: ROYAL } };
      periodCell.alignment = { horizontal: 'center' };

      const appliedFilters = [
        filterLabels.user && `Recurso: ${filterLabels.user}`,
        filterLabels.client && `Cliente: ${filterLabels.client}`,
        filterLabels.status && `Estatus: ${filterLabels.status}`,
        filterLabels.leader && `Líder: ${filterLabels.leader}`,
      ].filter(Boolean);
      ws.mergeCells(3, 1, 3, colCount);
      const filtersCell = ws.getCell(3, 1);
      filtersCell.value = `${appliedFilters.length ? `Filtros — ${appliedFilters.join('  |  ')}` : 'Sin filtros adicionales'}   ·   Generado el ${new Date().toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}`;
      filtersCell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
      filtersCell.alignment = { horizontal: 'center' };

      /* Header row */
      const headerRow = ws.getRow(4);
      columns.forEach((c, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = c.header;
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ROYAL } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = thinBorder;
      });
      headerRow.height = 22;

      /* Data rows — chronological order reads best in a business meeting */
      const sorted = [...filteredLogs].sort((a, b) =>
        a.log_date === b.log_date
          ? (a.start_time || '').localeCompare(b.start_time || '')
          : a.log_date.localeCompare(b.log_date)
      );

      sorted.forEach((l, idx) => {
        const row = ws.getRow(5 + idx);
        const total = round2((l.total_hours || 0) + (l.extended_hours || 0));
        const values = [
          formatDate(l.log_date),
          l.user?.full_name || '—',
          l.requirement?.reference_id || '—',
          l.requirement?.project?.client?.name || '—',
          l.requirement?.project?.name || '—',
          l.requirement?.leader?.full_name || '—',
          l.requirement?.status_rel?.name || '—',
          l.activity?.name || '—',
          l.activity?.hour_types?.name || '—',
          formatTime(l.start_time),
          formatTime(l.end_time),
          l.total_hours || 0,
          l.extended_hours || 0,
          total,
          l.description || '',
        ];
        values.forEach((v, i) => {
          const cell = row.getCell(i + 1);
          cell.value = v;
          cell.font = { size: 10 };
          cell.border = thinBorder;
          if (idx % 2 === 1) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
          }
          if (i >= 11 && i <= 13) {
            cell.numFmt = '0.00';
            cell.alignment = { horizontal: 'right' };
          } else if (i === 0 || (i >= 9 && i <= 10)) {
            cell.alignment = { horizontal: 'center' };
          }
        });
      });

      /* Totals row */
      const totalsRow = ws.getRow(5 + sorted.length);
      const sums = sorted.reduce(
        (acc, l) => {
          acc.normal += l.total_hours || 0;
          acc.ext += l.extended_hours || 0;
          return acc;
        },
        { normal: 0, ext: 0 }
      );
      totalsRow.getCell(1).value = 'TOTALES';
      totalsRow.getCell(12).value = round2(sums.normal);
      totalsRow.getCell(13).value = round2(sums.ext);
      totalsRow.getCell(14).value = round2(sums.normal + sums.ext);
      for (let i = 1; i <= colCount; i++) {
        const cell = totalsRow.getCell(i);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CARIBBEAN } };
        cell.font = { bold: true, size: 10, color: { argb: ROYAL } };
        cell.border = thinBorder;
        if (i >= 12 && i <= 14) {
          cell.numFmt = '0.00';
          cell.alignment = { horizontal: 'right' };
        }
      }

      ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4 + sorted.length, column: colCount } };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_de_Actividades_${dateFrom}_a_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('No se pudo generar el archivo de Excel. Intenta de nuevo.');
    } finally {
      setExporting(false);
    }
  }

  /* ── Render ───────────────────────────────────────── */
  return (
    <>
      <div className="act-report-view">
        {/* Header */}
        <div className="cat-crud-header">
          <button type="button" className="cat-back-btn" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Volver
          </button>
          <h1 className="cat-crud-title">Reporte de Actividades</h1>
          <button
            type="button"
            className="rep-export-btn rep-export-btn--excel"
            onClick={exportToExcel}
            disabled={exporting || loading || invalidRange || filteredLogs.length === 0}
            title={filteredLogs.length === 0 ? 'No hay registros para exportar' : 'Exportar a Excel'}
          >
            {exporting ? <span className="cat-spinner" /> : <IconDownload />}
            <span>{exporting ? 'Exportando…' : 'Exportar a Excel'}</span>
          </button>
        </div>

        {/* Summary cards */}
        <div className="rep-summary-row">
          <div className="act-summary-card">
            <span className="act-summary-label">Horas totales</span>
            <span className="act-summary-value">{summary.total}h</span>
          </div>
          <div className="act-summary-card">
            <span className="act-summary-label">Horas extendidas</span>
            <span className="act-summary-value">{summary.extended}h</span>
          </div>
          <div className="act-summary-card">
            <span className="act-summary-label">Registros</span>
            <span className="act-summary-value">{summary.records}</span>
          </div>
          <div className="act-summary-card">
            <span className="act-summary-label">Recursos</span>
            <span className="act-summary-value">{summary.resources}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="act-filters">
          <div className="act-filter-group">
            <label className="act-filter-label">Desde</label>
            <input
              type="date"
              className="cat-input"
              value={dateFrom}
              max={maxDate}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="act-filter-group">
            <label className="act-filter-label">Hasta</label>
            <input
              type="date"
              className="cat-input"
              value={dateTo}
              max={maxDate}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="act-filter-group">
            <label className="act-filter-label">Recurso</label>
            <button
              type="button"
              className={`act-fpm-trigger${filterUserId ? ' act-fpm-trigger--active' : ''}`}
              onClick={() => setUserPickerOpen(true)}
            >
              <span className="act-fpm-trigger-label">{filterLabels.user || 'Todos'}</span>
              <IconChevronDown />
            </button>
          </div>
          <div className="act-filter-group">
            <label className="act-filter-label">Cliente</label>
            <button
              type="button"
              className={`act-fpm-trigger${filterClientId ? ' act-fpm-trigger--active' : ''}`}
              onClick={() => setClientPickerOpen(true)}
            >
              <span className="act-fpm-trigger-label">{filterLabels.client || 'Todos'}</span>
              <IconChevronDown />
            </button>
          </div>
          <div className="act-filter-group">
            <label className="act-filter-label">Estatus</label>
            <select
              className="cat-select"
              value={filterStatusId}
              onChange={(e) => setFilterStatusId(e.target.value)}
            >
              <option value="">Todos</option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div className="act-filter-group">
            <label className="act-filter-label">Líder</label>
            <select
              className="cat-select"
              value={filterLeaderId}
              onChange={(e) => setFilterLeaderId(e.target.value)}
            >
              <option value="">Todos</option>
              {leaderOptions.map((l) => (
                <option key={l.id} value={l.id}>{l.full_name}</option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <div className="act-filter-group rep-clear-wrap">
              <button type="button" className="rep-clear-btn" onClick={clearFilters}>
                <IconClearFilter />
                <span>Limpiar filtros</span>
              </button>
            </div>
          )}
        </div>

        {invalidRange && (
          <div className="cat-error-banner rep-banner">
            La fecha "Desde" no puede ser posterior a la fecha "Hasta".
          </div>
        )}
        {fetchError && !invalidRange && (
          <div className="cat-error-banner rep-banner">{fetchError}</div>
        )}
        {exportError && (
          <div className="cat-error-banner rep-banner">{exportError}</div>
        )}

        {/* Table (read-only) */}
        {loading && !invalidRange ? (
          <div className="cat-loading">Cargando registros...</div>
        ) : invalidRange ? null : filteredLogs.length === 0 ? (
          <div className="cat-empty">
            {hasActiveFilters
              ? 'No se encontraron registros con los filtros seleccionados.'
              : 'No se encontraron registros en el período seleccionado.'}
          </div>
        ) : (
          <>
            <div className="cat-table-wrap">
              <table className="cat-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Recurso</th>
                    <th>Folio</th>
                    <th>Cliente</th>
                    <th>Líder</th>
                    <th>Estatus</th>
                    <th>Actividad</th>
                    <th>Tipo</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Horas</th>
                    <th>Ext.</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatDate(log.log_date)}</td>
                      <td>{log.user?.full_name || '—'}</td>
                      <td>{log.requirement?.reference_id || '—'}</td>
                      <td>{log.requirement?.project?.client?.name || '—'}</td>
                      <td>{log.requirement?.leader?.full_name || '—'}</td>
                      <td>
                        {log.requirement?.status_rel ? (
                          <span
                            className="rep-status-badge"
                            style={{
                              background: log.requirement.status_rel.color,
                              color: getContrastColor(log.requirement.status_rel.color),
                            }}
                          >
                            {log.requirement.status_rel.name}
                          </span>
                        ) : '—'}
                      </td>
                      <td>{log.activity?.name || '—'}</td>
                      <td>
                        <span className={`act-type-badge act-type-badge--${(log.activity?.hour_types?.name || '').replace(/[^a-zA-Z]/g, '').toLowerCase()}`}>
                          {log.activity?.hour_types?.name || '—'}
                        </span>
                      </td>
                      <td>{formatTime(log.start_time)}</td>
                      <td>{formatTime(log.end_time)}</td>
                      <td>{log.total_hours}h</td>
                      <td>{log.extended_hours ? `${log.extended_hours}h` : '—'}</td>
                      <td className="act-desc-cell" title={log.description || ''}>{log.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="cat-pagination-wrap">
                <span className="cat-page-info">
                  {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filteredLogs.length)} de {filteredLogs.length}
                </span>
                <div className="cat-pagination">
                  <button className="cat-page-nav" disabled={safePage === 0}
                    onClick={() => setPage(safePage - 1)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      className={`cat-page-btn ${i === safePage ? 'cat-page-btn--active' : ''}`}
                      onClick={() => setPage(i)}
                    >{i + 1}</button>
                  ))}
                  <button className="cat-page-nav" disabled={safePage >= totalPages - 1}
                    onClick={() => setPage(safePage + 1)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 6 15 12 9 18" /></svg>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Pickers (outside the animated wrapper, like TimeLogReport) ── */}
      <FilterPickerModal
        open={userPickerOpen}
        title="Filtrar por Recurso"
        options={users}
        value={filterUserId}
        onSelect={(id) => setFilterUserId(id)}
        onClose={() => setUserPickerOpen(false)}
        labelKey="full_name"
      />
      <FilterPickerModal
        open={clientPickerOpen}
        title="Filtrar por Cliente"
        options={clients}
        value={filterClientId}
        onSelect={(id) => setFilterClientId(id)}
        onClose={() => setClientPickerOpen(false)}
        labelKey="name"
      />
    </>
  );
}

export default ActivityReport;
